import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  getBodySystemAnalysisSettings,
  listAiModels,
  updateBodySystemAnalysisSettings,
} from '../../../api/chat';
import Button from '../../BaseComponents/Button';
import Select, { SelectOption } from '../../BaseComponents/Select';

const DESCRIPTION =
  'Select the AI model used for Body System Score and Summary generation. If none is selected, the platform default model is used.';

const BodySystemAnalysisPromptPanel = () => {
  const [modelOptions, setModelOptions] = useState<SelectOption[]>([]);
  const [selectedModel, setSelectedModel] = useState<SelectOption>({
    key: '',
    value: 'Loading models...',
  });
  const [defaultModelKey, setDefaultModelKey] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    void loadSettings();
  }, []);

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const [{ models, default_model_key, default_model_name }, settings] = await Promise.all([
        listAiModels(),
        getBodySystemAnalysisSettings(),
      ]);

      const activeModels = models.filter((model) => model.active);
      const resolvedDefaultKey = default_model_key?.trim() || '';
      const resolvedDefaultName = default_model_name?.trim() || resolvedDefaultKey;
      setDefaultModelKey(resolvedDefaultKey);

      const defaultOption: SelectOption = {
        key: '',
        value: resolvedDefaultName
          ? `${resolvedDefaultName} (Default)`
          : 'Default (env deployment)',
      };

      const options: SelectOption[] = [
        defaultOption,
        ...activeModels
          .filter((model) => model.key !== resolvedDefaultKey)
          .map((model) => ({
            key: model.key,
            value: model.name,
          })),
      ];
      setModelOptions(options);

      const savedKey =
        typeof settings.model_key === 'string' ? settings.model_key.trim() : '';

      if (savedKey && savedKey !== resolvedDefaultKey) {
        const matched = options.find((option) => String(option.key) === savedKey);
        setSelectedModel(matched || { key: savedKey, value: savedKey });
      } else {
        setSelectedModel(defaultOption);
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to load Body System Analysis settings. Please try again.';
      toast.error(message);
      setSelectedModel({ key: '', value: 'Default' });
      setModelOptions([{ key: '', value: 'Default' }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      const modelKey =
        selectedModel.key && String(selectedModel.key) !== defaultModelKey
          ? String(selectedModel.key)
          : null;
      await updateBodySystemAnalysisSettings({ model_key: modelKey });
      toast.success('Body System Analysis model saved successfully');
      await loadSettings();
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to save Body System Analysis settings. Please try again.';
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="prompts-form">
      <section className="prompt-field-card prompt-description-card">
        <h3 className="prompt-attachment-title">Description</h3>
        <p className="prompt-description-text">{DESCRIPTION}</p>
      </section>

      <section className="prompt-field-card prompt-model-section">
        <h3 className="prompt-attachment-title">AI Model</h3>
        {isLoading ? (
          <div className="prompts-loading prompts-loading--inline">Loading models...</div>
        ) : (
          <div className="prompt-model-select">
            <Select
              title="Model for Score & Summary generation"
              name="body_system_analysis_model"
              options={modelOptions}
              selectedOption={selectedModel}
              onChange={setSelectedModel}
              placeholder="Select AI model"
              isError={false}
              isDisable={isSaving || isLoading}
            />
          </div>
        )}
      </section>

      <div className="prompt-form-actions">
        <Button
          text={isSaving ? 'Saving…' : 'Save'}
          onClick={handleSave}
          isDisabled={isSaving || isLoading}
          width="140px"
          className="prompt-save-button"
        />
      </div>
    </div>
  );
};

export default BodySystemAnalysisPromptPanel;
